import { FormFieldConfig } from '@shared/components/search-form/search-form.component';
import { TableColumn } from '@shared/components/data-table/data-table.component';
import {
  FormField,
  TableSection,
  ActionButton,
} from '@shared/components/add-form/add-form.component';
import { STATUS_OPTIONS } from '../brand/brand.config';
import { ProductTaxMode } from '../shared/product-tax/product-tax.model';

// ============================================================================
// INTERFACES
// ============================================================================

// Base SPU interface
export interface BaseSpuItem {
  id: string;
  spuCode: string;
  name: string;
  type: string;
  categoryName: string;
  categoryId?: number;
  /** Secondary categories the SPU is also assigned to, in addition to categoryId (the primary). */
  additionalCategoryIds?: number[];
  materialName?: string;
  remark?: string;
  status: boolean;
  taxOverrideMode?: ProductTaxMode;
  taxId?: number | null;
  isPriceIncludeVAT?: boolean | null;
}

// Ordinary SPU interface
export interface OrdinarySpuItem extends BaseSpuItem {
  brandId?: string;
  brandName?: string;
  lastUpdateDate?: string;
}

// Package SPU interface
export interface PackageSpuItem extends BaseSpuItem {
  packageItems?: string[];
}

// SPU list response interface
export interface SpuListResponse<T extends BaseSpuItem> {
  data: T[];
  totalRecords: number;
  loading: boolean;
}

// SPU form configuration interface
export interface SpuFormConfig {
  title: string;
  formFields: any[];
  tableSections: any[];
  headerActions: any[];
  listRoute: string;
  spuType: string;
}

// SPU action types
export type SpuAction = 'view' | 'edit' | 'delete' | 'disable' | 'add';

// SPU form action event
export interface SpuFormActionEvent {
  action: string;
  data?: any;
}

// SPU table action event
export interface SpuTableActionEvent {
  section: string;
  action: string;
  data?: any;
}

// Property interfaces
export interface PropertyValue {
  id: number;
  value: string;
  sort: number;
  imageUrl?: string; // Add imageUrl field
  isSaved?: boolean;
  isEditing?: boolean;
}

export interface PropertyFormData {
  name: string;
  code: string;
  type: string;
  submissionsType: string;
  generateSku: boolean;
  remark: string;
  values: PropertyValue[];
}

// ============================================================================
// FORM FIELD OPTIONS
// ============================================================================

export const SPU_TYPE_OPTIONS = [
  { label: 'goods.spu.types.physical_product', value: 1 },
  { label: 'goods.spu.types.virtual_product', value: 2 },
];

export const PACKAGE_TYPE_OPTIONS = [
  { label: 'Bundle', value: 'BUNDLE' },
  { label: 'Gift Set', value: 'GIFT_SET' },
  { label: 'Combo', value: 'COMBO' },
];

// Display Type mappings
export const SUBMISSIONS_TYPES = {
  RADIO_BUTTON: 0,
  CHECKBOX: 1,
  TEXT_BOX: 2,
  DROPDOWN: 3,
} as const;

export const SUBMISSIONS_TYPES_LABELS = {
  [SUBMISSIONS_TYPES.RADIO_BUTTON]: 'common.form_types.radio_button',
  [SUBMISSIONS_TYPES.CHECKBOX]: 'common.form_types.checkbox',
  [SUBMISSIONS_TYPES.TEXT_BOX]: 'common.form_types.text_box',
  [SUBMISSIONS_TYPES.DROPDOWN]: 'common.form_types.dropdown',
} as const;

// Submissions Type mappings
export const TYPES = {
  PRODUCT_ATTRIBUTE: 0,
  SYSTEM_DEFAULT: 1,
  CATEGORY_ATTRIBUTE: 2,
  ADDITIONAL_ATTRIBUTE: 3,
} as const;

export const TYPE_LABELS = {
  [TYPES.PRODUCT_ATTRIBUTE]: 'goods.spu.attributes.fields.attribute_name',
  [TYPES.SYSTEM_DEFAULT]: 'goods.spu.attributes.fields.system_default',
  [TYPES.CATEGORY_ATTRIBUTE]: 'goods.spu.attributes.fields.category_attribute',
  [TYPES.ADDITIONAL_ATTRIBUTE]:
    'goods.spu.attributes.fields.additional_attribute',
} as const;

// Helper functions for type conversion
export const getSubmissionsTypeLabel = (
  type: number,
  translateService?: any
): string => {
  const key =
    SUBMISSIONS_TYPES_LABELS[type as keyof typeof SUBMISSIONS_TYPES_LABELS] ||
    'common.form_types.radio_button';
  return translateService ? translateService.instant(key) : key;
};

export const getSubmissionsTypeNumber = (
  label: string,
  translateService?: any
): number => {
  if (translateService) {
    for (const [key, i18nKey] of Object.entries(SUBMISSIONS_TYPES_LABELS)) {
      if (translateService.instant(i18nKey) === label) {
        return parseInt(key);
      }
    }
  } else {
    const entry = Object.entries(SUBMISSIONS_TYPES_LABELS).find(
      ([_, value]) => value === label
    );
    if (entry) return parseInt(entry[0]);
  }
  return SUBMISSIONS_TYPES.RADIO_BUTTON;
};

export const getTypeLabel = (type: number, translateService?: any): string => {
  const key =
    TYPE_LABELS[type as keyof typeof TYPE_LABELS] ||
    'goods.spu.attributes.fields.attribute_name';
  return translateService ? translateService.instant(key) : key;
};

export const getDisplayTypeNumber = (label: string): number => {
  const entry = Object.entries(TYPE_LABELS).find(
    ([_, value]) => value === label
  );
  return entry ? parseInt(entry[0]) : TYPES.PRODUCT_ATTRIBUTE;
};

// Form options
export const SUBMISSIONS_TYPE_OPTIONS = Object.entries(
  SUBMISSIONS_TYPES_LABELS
).map(([key, label]) => ({
  label,
  value: label,
}));

// Function to get translated options
export const getSubmissionsTypeOptions = (translateService?: any) => {
  return Object.entries(SUBMISSIONS_TYPES_LABELS).map(([key, i18nKey]) => {
    const translatedLabel = translateService
      ? translateService.instant(i18nKey)
      : i18nKey;
    return {
      label: translatedLabel,
      value: translatedLabel, // Use translated label as value for form matching
    };
  });
};

// ============================================================================
// SEARCH FORM CONFIGURATIONS
// ============================================================================

export const ORDINARY_SPU_SEARCH_CONFIG: FormFieldConfig[] = [
  {
    key: 'name',
    label: 'goods.spu.search.fields.product_name',
    type: 'text',
    placeholder: 'goods.spu.search.placeholders.enter_product_name',
  },
  {
    key: 'spuCode',
    label: 'goods.spu.search.fields.product_code',
    type: 'text',
    placeholder: 'goods.spu.search.placeholders.enter_product_code',
  },
  {
    key: 'type',
    label: 'goods.spu.search.fields.product_type',
    type: 'select',
    options: SPU_TYPE_OPTIONS,
    placeholder: 'goods.spu.search.placeholders.select_product_type',
    showClear: true,
  },
  {
    key: 'categoryIdList',
    label: 'goods.spu.search.fields.category',
    type: 'treeselect',
    treeOptions: [],
    placeholder: 'goods.spu.search.placeholders.select_category',
  },
  {
    key: 'brandId',
    label: 'goods.spu.search.fields.brand',
    type: 'select',
    options: [],
    placeholder: 'goods.spu.search.placeholders.select_brand',
  },
  // { key: 'materialName', label: 'Product Asset Name', type: 'select', placeholder: 'Select product asset name' }
  {
    key: 'status',
    label: 'goods.spu.search.fields.status',
    type: 'select',
    options: STATUS_OPTIONS,
    placeholder: 'goods.spu.search.placeholders.select_status',
    showClear: true,
  },
];

export const PACKAGE_SPU_SEARCH_CONFIG: FormFieldConfig[] = [
  {
    key: 'name',
    label: 'goods.spu.search.fields.product_name',
    type: 'text',
    placeholder: 'goods.spu.search.placeholders.enter_product_name',
  },
  {
    key: 'spuCode',
    label: 'goods.spu.search.fields.product_code',
    type: 'text',
    placeholder: 'goods.spu.search.placeholders.enter_product_code',
  },
  {
    key: 'type',
    label: 'goods.spu.search.fields.product_type',
    type: 'select',
    options: SPU_TYPE_OPTIONS,
    placeholder: 'goods.spu.search.placeholders.select_product_type',
  },
  {
    key: 'categoryIdList',
    label: 'goods.spu.search.fields.category',
    type: 'treeselect',
    treeOptions: [],
    placeholder: 'goods.spu.search.placeholders.select_category',
  },
  // { key: 'materialName', label: 'Product Asset Name', type: 'select', placeholder: 'Select product asset name' },
  {
    key: 'status',
    label: 'goods.spu.search.fields.status',
    type: 'select',
    options: STATUS_OPTIONS,
    placeholder: 'goods.spu.search.placeholders.select_status',
  },
];

// ============================================================================
// TABLE COLUMN CONFIGURATIONS
// ============================================================================

/**
 * Default column property names (shown on first load)
 * Update these constants to change column property names
 */
export const ORDINARY_SPU_DEFAULT_COLUMNS = {
  CATEGORY: 'categoryName',
  CODE: 'spuCode',
  NAME: 'name',
  TYPE: 'type',
  BRAND: 'brandName',
  MEDIA: 'materialName',
  STATUS: 'status',
} as const;

/**
 * Flexible column property names (can be shown/hidden via column selector)
 * Update these constants to change flexible column property names
 */
export const ORDINARY_SPU_FLEXIBLE_COLUMNS = {
  REMARK: 'remark',
  LAST_UPDATE_DATE: 'lastUpdateDate',
} as const;

/**
 * Action column property name
 */
export const ORDINARY_SPU_ACTION_COLUMN = {
  ACTIONS: 'actions',
} as const;

/**
 * Default columns order (shown on first load)
 * Update this array to change the default column order
 */
export const ORDINARY_SPU_DEFAULT_COLUMNS_ORDER: string[] = [
  ORDINARY_SPU_DEFAULT_COLUMNS.CATEGORY,
  ORDINARY_SPU_DEFAULT_COLUMNS.CODE,
  ORDINARY_SPU_DEFAULT_COLUMNS.NAME,
  ORDINARY_SPU_DEFAULT_COLUMNS.TYPE,
  ORDINARY_SPU_DEFAULT_COLUMNS.BRAND,
  ORDINARY_SPU_DEFAULT_COLUMNS.MEDIA,
  ORDINARY_SPU_DEFAULT_COLUMNS.STATUS,
];

/**
 * Flexible columns array (can be shown/hidden via column selector)
 * Update this array to change which columns are flexible
 */
export const ORDINARY_SPU_FLEXIBLE_COLUMNS_ORDER: string[] = [
  ORDINARY_SPU_FLEXIBLE_COLUMNS.REMARK,
  ORDINARY_SPU_FLEXIBLE_COLUMNS.LAST_UPDATE_DATE,
];

export const ORDINARY_SPU_COLUMNS: TableColumn[] = [
  {
    label: 'goods.spu.table.columns.category',
    prop: ORDINARY_SPU_DEFAULT_COLUMNS.CATEGORY,
    width: 100,
    type: 'custom',
    template: 'categoryTemplate',
  },
  {
    label: 'goods.spu.table.columns.product_code',
    prop: ORDINARY_SPU_DEFAULT_COLUMNS.CODE,
    width: 100,
  },
  {
    label: 'goods.spu.table.columns.product_name',
    prop: ORDINARY_SPU_DEFAULT_COLUMNS.NAME,
    width: 140,
  },
  {
    label: 'goods.spu.table.columns.product_type',
    prop: ORDINARY_SPU_DEFAULT_COLUMNS.TYPE,
    width: 80,
    type: 'custom',
  },
  {
    label: 'goods.spu.table.columns.brand',
    prop: ORDINARY_SPU_DEFAULT_COLUMNS.BRAND,
    width: 100,
  },
  {
    label: 'goods.spu.table.columns.material',
    prop: ORDINARY_SPU_DEFAULT_COLUMNS.MEDIA,
    width: 120,
  },
  {
    label: 'goods.spu.table.columns.status',
    prop: ORDINARY_SPU_DEFAULT_COLUMNS.STATUS,
    width: 50,
    type: 'status',
  },
  {
    label: 'goods.spu.table.columns.remark',
    prop: ORDINARY_SPU_FLEXIBLE_COLUMNS.REMARK,
    width: 120,
  },
  {
    label: 'goods.spu.table.columns.last_update_date',
    prop: ORDINARY_SPU_FLEXIBLE_COLUMNS.LAST_UPDATE_DATE,
    width: 150,
    type: 'custom',
    template: 'dateTemplate',
  },
  {
    label: 'goods.spu.table.columns.actions',
    prop: ORDINARY_SPU_ACTION_COLUMN.ACTIONS,
    type: 'actions',
    width: 200,
  },
];

export const PACKAGE_SPU_COLUMNS: TableColumn[] = [
  {
    label: 'goods.spu.table.columns.category',
    prop: 'categoryName',
    width: 120,
    type: 'custom',
    template: 'categoryTemplate',
  },
  { label: 'goods.spu.table.columns.product_name', prop: 'name', width: 160 },
  {
    label: 'goods.spu.table.columns.product_code',
    prop: 'spuCode',
    width: 120,
  },
  { label: 'goods.spu.table.columns.product_type', prop: 'type', width: 80 },
  {
    label: 'goods.spu.table.columns.material',
    prop: 'materialName',
    width: 120,
  },
  { label: 'goods.spu.table.columns.remark', prop: 'remark', width: 120 },
  {
    label: 'goods.spu.table.columns.status',
    prop: 'status',
    width: 50,
    type: 'status',
  },
  {
    label: 'goods.spu.table.columns.actions',
    prop: 'actions',
    type: 'actions',
    width: 200,
  },
];

// Property Table Column Configurations (matches demo exactly)
export const PROPERTY_TABLE_COLUMNS: TableColumn[] = [
  {
    label: 'goods.spu.attributes.fields.property_name',
    prop: 'name',
    width: 120,
  },
  {
    label: 'goods.spu.attributes.fields.property_code',
    prop: 'code',
    width: 120,
  },
  {
    label: 'goods.spu.attributes.fields.display_type',
    prop: 'submissionsType',
    width: 120,
  },
  {
    label: 'goods.spu.attributes.fields.property_values',
    prop: 'values',
    width: 200,
  },
  {
    label: 'goods.spu.attributes.fields.property_asset',
    prop: 'propertyAsset',
    width: 150,
    type: 'custom',
  },
  {
    label: 'goods.spu.attributes.fields.property_type',
    prop: 'type',
    width: 120,
  },
  { label: 'goods.spu.attributes.fields.remarks', prop: 'remark', width: 150 },
  {
    label: 'goods.spu.attributes.fields.generate_sku',
    prop: 'generateSku',
    width: 100,
    type: 'custom',
  },
  {
    label: 'goods.spu.table.columns.actions',
    prop: 'actions',
    type: 'actions',
    width: 120,
  },
];

// Property Table Column Configurations for Package SPUs (without Generate SKU)
export const PACKAGE_PROPERTY_TABLE_COLUMNS: TableColumn[] = [
  {
    label: 'goods.spu.attributes.fields.property_name',
    prop: 'name',
    width: 120,
  },
  {
    label: 'goods.spu.attributes.fields.property_code',
    prop: 'code',
    width: 120,
  },
  {
    label: 'goods.spu.attributes.fields.display_type',
    prop: 'submissionsType',
    width: 120,
  },
  {
    label: 'goods.spu.attributes.fields.property_values',
    prop: 'values',
    width: 200,
  },
  {
    label: 'goods.spu.attributes.fields.property_asset',
    prop: 'propertyAsset',
    width: 150,
    type: 'custom',
  },
  {
    label: 'goods.spu.attributes.fields.property_type',
    prop: 'type',
    width: 120,
  },
  { label: 'goods.spu.attributes.fields.remarks', prop: 'remark', width: 150 },
  {
    label: 'goods.spu.table.columns.actions',
    prop: 'actions',
    type: 'actions',
    width: 120,
  },
];

// ============================================================================
// BUTTON CONFIGURATIONS
// ============================================================================

export const ORDINARY_SPU_BUTTON_CONFIG = {
  label: 'common.actions.new',
  severity: 'info' as const,
  show: true,
  icon: 'add',
};

export const PACKAGE_SPU_BUTTON_CONFIG = {
  label: 'common.actions.new',
  severity: 'info' as const,
  show: true,
  icon: 'add',
};

// ============================================================================
// FORM FIELD CONFIGURATIONS
// ============================================================================

export const COMMON_SPU_FORM_FIELDS: FormField[] = [
  {
    label: 'goods.spu.form.fields.combination_type',
    controlName: 'combinationType',
    type: 'text',
    placeholder: 'goods.spu.form.placeholders.package_product',
    readonly: true,
    defaultValue: 'goods.spu.form.placeholders.package_product',
    section: 'goods.spu.form.sections.product_information',
  },
  {
    label: 'goods.spu.form.fields.product_name',
    controlName: 'name',
    type: 'text',
    placeholder: 'goods.spu.form.placeholders.enter_product_name',
    required: true,
    section: 'goods.spu.form.sections.product_information',
    hint: 'goods.spu.form.hints.no_specs_in_name',
    translateHint: true,
    hintAsTooltip: true,
    maxLength: 255,
    errorMessages: {
      required: 'goods.spu.form.validation.product_name_required',
      maxlength: 'goods.spu.form.validation.product_name_maxlength',
    },
  },
  {
    label: 'goods.spu.form.fields.product_type',
    controlName: 'type',
    type: 'radio',
    options: SPU_TYPE_OPTIONS,
    required: true,
    defaultValue: 1,
    section: 'goods.spu.form.sections.product_information',
  },
  {
    label: 'goods.spu.form.fields.product_code',
    controlName: 'spuCode',
    type: 'text',
    placeholder: 'goods.spu.form.placeholders.enter_product_code',
    required: true,
    section: 'goods.spu.form.sections.product_information',
    maxLength: 255,
    errorMessages: {
      required: 'goods.spu.form.validation.product_code_required',
      maxlength: 'goods.spu.form.validation.product_code_maxlength',
    },
  },
  {
    label: 'goods.spu.form.fields.product_category',
    controlName: 'categoryName',
    type: 'treeselect',
    treeOptions: [], // Will be populated dynamically from API using getCategoryTree
    required: true,
    useCheckbox: false,
    placeholder: 'goods.spu.form.placeholders.select_product_category',
    section: 'goods.spu.form.sections.product_information',
  },
  {
    label: 'goods.spu.form.fields.additional_categories',
    controlName: 'additionalCategoryIds',
    type: 'treeselect',
    treeOptions: [], // Will be populated dynamically from API using getCategoryTree
    useCheckbox: true, // Multi-select: secondary categories in addition to the primary one above
    defaultValue: [],
    placeholder: 'goods.spu.form.placeholders.select_additional_categories',
    section: 'goods.spu.form.sections.product_information',
  },
  {
    label: 'goods.spu.form.fields.remarks',
    controlName: 'remark',
    type: 'text',
    placeholder: 'goods.spu.form.placeholders.enter_remarks',
    section: 'goods.spu.form.sections.product_information',
    hint: 'goods.spu.form.hints.no_specs_in_remark',
    translateHint: true,
    hintAsTooltip: true,
    maxLength: 255,
    errorMessages: {
      maxlength: 'goods.spu.form.validation.remarks_maxlength',
    },
  },
  {
    label: 'goods.spu.form.fields.serial_profile',
    controlName: 'serialProfileId',
    type: 'dropdown' as const,
    placeholder: 'goods.spu.form.placeholders.select_serial_profile',
    section: 'goods.spu.form.sections.product_information',
    showClear: true,
    options: [], // Will be populated dynamically from getAllSerialProfile API
  },
  {
    label: 'goods.spu.form.fields.product_asset_name',
    controlName: 'materialName',
    type: 'dropdown',
    placeholder: 'goods.spu.form.placeholders.select_product_asset',
    section: 'goods.spu.form.sections.product_images',
    required: true,
    showClear: true,
    options: [], // Will be populated dynamically
  },
  {
    label: 'goods.spu.form.fields.main_image',
    controlName: 'mainImage',
    type: 'galleria-display',
    section: 'goods.spu.form.sections.product_images',
    placeholder: 'goods.spu.form.placeholders.image_from_product_asset',
    maxFiles: 10,
    dynamicLabel: {
      type: 'image-count',
      singularLabel: 'goods.spu.form.fields.main_image',
      pluralLabel: 'goods.spu.form.fields.main_image',
    },
  },
  {
    label: 'goods.spu.form.fields.detail_images',
    controlName: 'detailedImage',
    type: 'galleria-display',
    section: 'goods.spu.form.sections.product_images',
    placeholder: 'goods.spu.form.placeholders.image_from_product_asset',
    maxFiles: 10,
    dynamicLabel: {
      type: 'image-count',
      singularLabel: 'goods.spu.form.fields.detail_images',
      pluralLabel: 'goods.spu.form.fields.detail_images',
    },
  },
  {
    label: 'goods.spu.form.fields.usage_video',
    controlName: 'usageVideo',
    type: 'galleria-display',
    section: 'goods.spu.form.sections.product_images',
    placeholder: 'goods.spu.form.placeholders.video_from_product_asset',
  },
  {
    label: 'goods.spu.form.fields.active',
    controlName: 'status',
    type: 'checkbox',
    defaultValue: false,
    section: 'goods.spu.form.sections.product_information',
    colspan: 3,
  },
];

// ============================================================================
// TABLE SECTION CONFIGURATIONS
// ============================================================================

export const SPU_PROPERTY_TABLE_SECTION: TableSection = {
  title: 'goods.spu.attributes.property_table',
  show: true,
  columns: PROPERTY_TABLE_COLUMNS,
  data: [],
  actions: [
    {
      label: 'common.actions.delete',
      action: 'deleteSelected',
      severity: 'danger',
      outlined: true,
      disabled: true,
    },
    {
      label: 'goods.spu.actions.add_property',
      action: 'addProperty',
      severity: 'info',
    },
  ],
  hasCheckbox: true,
};

export const PACKAGE_SPU_PROPERTY_TABLE_SECTION: TableSection = {
  title: 'goods.spu.attributes.property_table',
  show: true,
  columns: PACKAGE_PROPERTY_TABLE_COLUMNS,
  data: [],
  actions: [
    {
      label: 'common.actions.delete',
      action: 'deleteSelected',
      severity: 'danger',
      outlined: true,
      disabled: true,
    },
    {
      label: 'goods.spu.actions.add_property',
      action: 'addProperty',
      severity: 'info',
    },
  ],
  hasCheckbox: true,
};

// ============================================================================
// HEADER ACTIONS
// ============================================================================

export const SPU_HEADER_ACTIONS: ActionButton[] = [
  {
    label: 'common.actions.cancel',
    action: 'cancel',
    outlined: true,
    severity: 'secondary',
  },
  { label: 'common.actions.save', action: 'save', severity: 'info' },
];

// View mode header actions - only Cancel button for read-only forms
export const SPU_VIEW_MODE_HEADER_ACTIONS: ActionButton[] = [
  {
    label: 'common.actions.cancel',
    action: 'cancel',
    outlined: true,
    severity: 'secondary',
  },
];

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

export function createSpuFormFields(
  isPackage: boolean,
  translateService?: any
): FormField[] {
  const baseFields = [...COMMON_SPU_FORM_FIELDS];

  // Update combination type based on SPU type
  const combinationTypeField = baseFields.find(
    (field) => field.controlName === 'combinationType'
  );
  if (combinationTypeField) {
    const packageProductKey = 'goods.spu.form.placeholders.package_product';
    const standardProductKey = 'goods.spu.form.placeholders.standard_product';

    combinationTypeField.defaultValue = isPackage
      ? translateService
        ? translateService.instant(packageProductKey)
        : packageProductKey
      : translateService
      ? translateService.instant(standardProductKey)
      : standardProductKey;
    combinationTypeField.placeholder = isPackage
      ? translateService
        ? translateService.instant(packageProductKey)
        : packageProductKey
      : translateService
      ? translateService.instant(standardProductKey)
      : standardProductKey;
  }

  if (!isPackage) {
    // Add brand name field for ordinary SPU at position length-1 (second-to-last)
    const brandNameField = {
      label: 'goods.spu.form.fields.brand_name',
      controlName: 'brandName',
      type: 'dropdown' as const,
      options: [], // Will be populated dynamically from API
      placeholder: 'goods.spu.form.placeholders.select_brand',
      section: 'goods.spu.form.sections.product_information',
      showClear: true,
    };

    // Insert at second-to-last position
    baseFields.splice(baseFields.length - 1, 0, brandNameField);
  }

  return baseFields;
}
